import { Card, CardContent, Typography, useTheme } from '@mui/joy';
import { ChartOptions } from 'chart.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { HorizontalBarChart, ITssDashboardFilters } from 'reports/components';
import { CHART_COLORS } from 'reports/constants';
import { useGetBomHeader } from 'reports/hooks';
import { IBomHeaderJob } from 'reports/types';
import { Level, ModeType, Variant } from 'theme/enums';

const DEFAULT_LIMIT = 10;
const PERCENTAGE_MIN = -100;
const PERCENTAGE_MAX = 100;

interface IProps {
  title: string;
  currentYearStart: string;
  currentYearEnd: string;
  previousYearStart: string;
  previousYearEnd: string;
  filters?: ITssDashboardFilters;
  height: number | string;
  limit?: number;
}

export const JobsByEngineerChart = ({
  title,
  currentYearStart,
  currentYearEnd,
  previousYearStart,
  previousYearEnd,
  filters,
  height,
  limit = DEFAULT_LIMIT,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === ModeType.DARK;
  const previousYearColor = isDarkMode
    ? CHART_COLORS.previousYear.dark
    : CHART_COLORS.previousYear.light;

  const currentYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: currentYearStart,
        jobCreatedAtUntil: currentYearEnd,
      },
    }),
    [currentYearStart, currentYearEnd, filters]
  );

  const previousYearPayload = useMemo(
    () => ({
      filters: {
        ...filters,
        jobCreatedAtSince: previousYearStart,
        jobCreatedAtUntil: previousYearEnd,
      },
    }),
    [previousYearStart, previousYearEnd, filters]
  );

  const {
    records: currentYearRecords,
    isLoading: isLoadingCurrentYear,
    isFetching: isFetchingCurrentYear,
  } = useGetBomHeader(currentYearPayload);
  const {
    records: previousYearRecords,
    isLoading: isLoadingPreviousYear,
    isFetching: isFetchingPreviousYear,
  } = useGetBomHeader(previousYearPayload);

  const isLoading =
    isLoadingCurrentYear ||
    isLoadingPreviousYear ||
    isFetchingCurrentYear ||
    isFetchingPreviousYear;

  const transformedData = useMemo(() => {
    if (!currentYearRecords.length && !previousYearRecords.length) {
      return { datasets: [], labels: [] };
    }

    const engineerCounts: Record<string, { currentYear: number; previousYear: number }> = {};

    countJobsByEngineer(previousYearRecords, engineerCounts, 'previousYear', t('labelUnknown'));
    countJobsByEngineer(currentYearRecords, engineerCounts, 'currentYear', t('labelUnknown'));

    const sortedEngineers = Object.entries(engineerCounts)
      .sort(
        ([, engineerACounts], [, engineerBCounts]) =>
          engineerACounts.currentYear +
          engineerACounts.previousYear -
          (engineerBCounts.currentYear + engineerBCounts.previousYear)
      )
      .reverse()
      .slice(0, limit);

    const labels = sortedEngineers.map(([name]) => name);
    const previousYearPercentages = sortedEngineers.map(([, counts]) => {
      const total = counts.previousYear + counts.currentYear;

      return calculatePercentage(counts.previousYear, total, true);
    });
    const currentYearPercentages = sortedEngineers.map(([, counts]) => {
      const total = counts.previousYear + counts.currentYear;

      return calculatePercentage(counts.currentYear, total);
    });

    return {
      datasets: [
        {
          backgroundColor: previousYearColor,
          data: previousYearPercentages,
          label: t('labelPreviousYearCases'),
        },
        {
          backgroundColor: CHART_COLORS.currentYear,
          data: currentYearPercentages,
          label: t('labelCurrentYearCases'),
        },
      ],
      labels,
    };
  }, [currentYearRecords, limit, previousYearColor, previousYearRecords, t]);

  const chartOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = Math.abs(context.parsed.x);

              return `${label}: ${value}%`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: (context) =>
              context.tick.value === 0 ? theme.palette.neutral.softOutlined : 'transparent',
          },
          max: PERCENTAGE_MAX,
          min: PERCENTAGE_MIN,
          ticks: {
            callback: (value) => `${Math.abs(Number(value))}%`,
          },
        },
      },
    }),
    [theme]
  );

  return (
    <Card variant={Variant.OUTLINED}>
      <CardContent>
        <Typography level={Level.TITLE_MD} sx={{ marginBottom: 2 }}>
          {title}
        </Typography>
        <HorizontalBarChart
          data={transformedData}
          isLoading={isLoading}
          height={height}
          options={chartOptions}
        />
      </CardContent>
    </Card>
  );
};

const calculatePercentage = (count: number, total: number, isNegative = false): number => {
  if (total === 0) {
    return 0;
  }

  const percentage = Math.round((count / total) * 100);

  return isNegative ? -percentage : percentage;
};

const countJobsByEngineer = (
  jobs: IBomHeaderJob[],
  counts: Record<string, { currentYear: number; previousYear: number }>,
  key: keyof Record<string, { currentYear: number; previousYear: number }>,
  unknownLabel: string
) => {
  jobs.forEach((job) => {
    const engineerName = job.creatorName || unknownLabel;

    if (!counts[engineerName]) {
      counts[engineerName] = { currentYear: 0, previousYear: 0 };
    }

    counts[engineerName][key]++;
  });
};
